import { useState } from 'react'
import { useApp } from '../context/AppContext'
import { persistAppendOnlyVersion, persistDeletedVersion, persistDeletedVersions } from '../lib/lumiAppendOnly'
import { formatCurrency, gerarCodigo, nowISO } from '../utils'
import type { Aprovacao, ContaPagar, ItemCotacao, ItemOrdemCompra, OrdemCompra } from '../types'

const STATUS_COLOR: Record<string, string> = {
  EM_APROVACAO: '#fbbf24',
  APROVADA: '#a78bfa',
  EMITIDA: '#60a5fa',
  PARCIALMENTE_RECEBIDA: '#34d399',
  RECEBIDA: '#4ade80',
  CANCELADA: '#6b7280',
}

function itemCotacaoValorUnitario(item: ItemCotacao) {
  return Number(item.valor_unitario_cotado ?? item.valor_unitario ?? 0)
}

function itemCotacaoValorTotal(item: ItemCotacao) {
  return Number(item.valor_total_cotado ?? item.valor_total ?? 0)
}

function itemCotacaoVencedor(item: ItemCotacao) {
  return Boolean(item.fornecedor_selecionado ?? item.vencedor)
}

export default function OrdensCompra() {
  const {
    ordensCompra, setOrdensCompra, itensOrdemCompra, setItensOrdemCompra, cotacoes,
    itensCotacao, requisicoes, setRequisicoes, itensRequisicao, fornecedores, obras, centrosCusto,
    itensOrcados, setItensOrcados, contasPagar, setContasPagar, aprovacoes, setAprovacoes, currentUser, addLog,
  } = useApp()
  const [modal, setModal] = useState(false)
  const [editId, setEditId] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [toast, setToast] = useState('')
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [form, setForm] = useState({ cotacao_id: '', data_emissao: new Date().toISOString().slice(0, 10), condicao_pagamento: '30 dias', previsao_entrega: '', observacoes: '' })

  const cotConcluidas = cotacoes.filter(c => c.status === 'CONCLUIDA' && c.fornecedor_vencedor_id && cotacaoTemItensVencedores(c.id))

  function showToast(msg: string) {
    setToast(msg)
    setTimeout(() => setToast(''), 3000)
  }

  function cotacaoTotal(cotacaoId: string) {
    const cot = cotacoes.find(c => c.id === cotacaoId)
    if (!cot?.fornecedor_vencedor_id) return 0
    return itensCotacao
      .filter(i => i.cotacao_id === cotacaoId && i.fornecedor_id === cot.fornecedor_vencedor_id && itemCotacaoVencedor(i))
      .reduce((s, i) => s + itemCotacaoValorTotal(i), 0)
  }

  function cotacaoTemItensVencedores(cotacaoId: string) {
    const cot = cotacoes.find(c => c.id === cotacaoId)
    if (!cot?.fornecedor_vencedor_id) return false
    return itensCotacao.some(i => i.cotacao_id === cotacaoId && i.fornecedor_id === cot.fornecedor_vencedor_id && itemCotacaoVencedor(i) && itemCotacaoValorTotal(i) > 0)
  }

  function buildItensOC(ocId: string, cotacaoId: string): ItemOrdemCompra[] {
    const cot = cotacoes.find(c => c.id === cotacaoId)
    const vencedores = itensCotacao.filter(i => i.cotacao_id === cotacaoId && i.fornecedor_id === cot?.fornecedor_vencedor_id && itemCotacaoVencedor(i))
    return vencedores.map((item, index) => {
      const itemReq = itensRequisicao.find(i => i.id === item.item_requisicao_id)
      const itemOrcado = itensOrcados.find(i => i.id === itemReq?.item_orcado_id)
      const descricao = itemReq?.descricao || 'Item da OC'
      const valorUnitario = itemCotacaoValorUnitario(item)
      const valorTotal = itemCotacaoValorTotal(item)
      return {
        id: `ioc${Date.now()}${index}`,
        ordem_compra_id: ocId,
        item_requisicao_id: item.item_requisicao_id,
        item_orcado_id: itemReq?.item_orcado_id || '',
        categoria_orcamentaria_id: itemOrcado?.categoria_orcamentaria_id || '',
        descricao,
        descricao_item: descricao,
        unidade_medida: itemReq?.unidade_medida || '',
        quantidade: item.quantidade,
        valor_unitario: valorUnitario,
        valor_total: valorTotal,
        saldo_recebimento: item.quantidade,
        status: 'PENDENTE',
        createdAt: nowISO(),
      }
    })
  }

  function validate() {
    const e: Record<string, string> = {}
    if (!form.cotacao_id) e.cotacao_id = 'Cotacao obrigatoria'
    if (!form.previsao_entrega) e.previsao_entrega = 'Previsao de entrega obrigatoria'
    if (form.cotacao_id && cotacaoTotal(form.cotacao_id) <= 0) e.cotacao_id = 'Cotacao sem itens vencedores.'
    if (form.cotacao_id && ordensCompra.some(oc => oc.cotacao_id === form.cotacao_id && oc.status !== 'CANCELADA' && oc.id !== editId)) {
      e.cotacao_id = 'Ja existe uma OC ativa para esta cotacao.'
    }
    return e
  }

  function openNew() {
    setEditId(null)
    setForm({ cotacao_id: '', data_emissao: new Date().toISOString().slice(0, 10), condicao_pagamento: '30 dias', previsao_entrega: '', observacoes: '' })
    setErrors({})
    setModal(true)
  }

  function openEdit(oc: OrdemCompra) {
    setEditId(oc.id)
    setForm({
      cotacao_id: oc.cotacao_id,
      data_emissao: oc.data_emissao,
      condicao_pagamento: oc.condicao_pagamento,
      previsao_entrega: oc.previsao_entrega,
      observacoes: oc.observacoes || '',
    })
    setErrors({})
    setModal(true)
  }

  async function save() {
    const e = validate()
    if (Object.keys(e).length) {
      setErrors(e)
      return
    }
    const cot = cotacoes.find(c => c.id === form.cotacao_id)
    const req = requisicoes.find(r => r.id === cot?.requisicao_id)
    if (!cot || !req || !cot.fornecedor_vencedor_id) {
      setErrors({ cotacao_id: 'Cotacao invalida.' })
      return
    }
    setSaving(true)
    if (editId) {
      const atual = ordensCompra.find(item => item.id === editId)
      if (!atual) {
        setSaving(false)
        return
      }
      const atualizada: OrdemCompra = {
        ...atual,
        cotacao_id: cot.id,
        fornecedor_id: cot.fornecedor_vencedor_id,
        obra_id: req.obra_id,
        centro_custo_id: req.centro_custo_id,
        data_emissao: form.data_emissao,
        condicao_pagamento: form.condicao_pagamento,
        previsao_entrega: form.previsao_entrega,
        observacoes: form.observacoes,
      }
      try {
        await persistAppendOnlyVersion('ordens_compra', atualizada as unknown as Record<string, unknown>)
      } catch {
        showToast('Erro, tente novamente mais tarde.')
        setSaving(false)
        return
      }
      setOrdensCompra(prev => prev.map(item => item.id === editId ? atualizada : item))
      addLog('ORDEM_COMPRA', editId, 'EDITADO', `OC ${atual.numero_oc} atualizada`)
      showToast('OC atualizada.')
      setModal(false)
      setEditId(null)
      setSaving(false)
      return
    }

    const ocId = 'oc' + Date.now()
    const itens = buildItensOC(ocId, cot.id)
    const valorTotal = itens.reduce((s, item) => s + item.valor_total, 0)
    const nova: OrdemCompra = {
      id: ocId,
      cotacao_id: cot.id,
      fornecedor_id: cot.fornecedor_vencedor_id,
      obra_id: req.obra_id,
      centro_custo_id: req.centro_custo_id,
      numero_oc: gerarCodigo(),
      data_emissao: form.data_emissao,
      valor_total: valorTotal,
      condicao_pagamento: form.condicao_pagamento,
      previsao_entrega: form.previsao_entrega,
      status: 'EM_APROVACAO',
      observacoes: form.observacoes,
      createdAt: nowISO(),
    }
    const ap: Aprovacao = {
      id: 'ap' + Date.now(),
      entidade_tipo: 'ORDEM_COMPRA',
      entidade_id: ocId,
      etapa_aprovacao: 'FINANCEIRO',
      aprovador_nome: '',
      perfil_aprovador: 'FINANCEIRO',
      solicitante_usuario_id: currentUser?.id,
      solicitante_nome: currentUser?.nome,
      solicitante_perfil: currentUser?.perfil,
      status: 'PENDENTE',
      createdAt: nowISO(),
    }
    try {
      await persistAppendOnlyVersion('ordens_compra', nova as unknown as Record<string, unknown>)
      await Promise.all(itens.map(item => persistAppendOnlyVersion('itens_ordem_compra', item as unknown as Record<string, unknown>)))
      await persistAppendOnlyVersion('aprovacoes', ap as unknown as Record<string, unknown>)
    } catch (error) {
      console.error('Falha ao confirmar OC no Lumi:', error)
      showToast('Erro, tente novamente mais tarde.')
      setSaving(false)
      return
    }
    setOrdensCompra(prev => [...prev, nova])
    setItensOrdemCompra(prev => [...prev, ...itens])
    setAprovacoes(prev => [ap, ...prev])
    addLog('ORDEM_COMPRA', nova.id, 'CRIADO', `OC ${nova.numero_oc} criada com ${itens.length} item(ns) e enviada para aprovacao`)
    showToast('OC criada e enviada para aprovacao financeira.')
    setModal(false)
    setSaving(false)
  }

  function estornarComprometido(oc: OrdemCompra) {
    if (!['EMITIDA', 'PARCIALMENTE_RECEBIDA', 'RECEBIDA'].includes(oc.status)) return

    const itens = itensOrdemCompra.filter(i => i.ordem_compra_id === oc.id)
    setItensOrcados(prev => prev.map(item => {
      const valorComprometido = itens.filter(i => i.item_orcado_id === item.id).reduce((s, i) => s + i.valor_total, 0)
      if (!valorComprometido) return item
      const novoComp = Math.max(0, item.valor_comprometido - valorComprometido)
      return { ...item, valor_comprometido: novoComp, saldo_atual: item.valor_total_previsto - novoComp - item.valor_pago }
    }))
  }

  async function deleteOrdem(oc: OrdemCompra) {
    const confirmed = window.confirm(`Excluir a OC ${oc.numero_oc}? Esta acao remove itens, aprovacoes e contas a pagar vinculadas.`)
    if (!confirmed) return

    try {
      const itensVinculados = itensOrdemCompra.filter(item => item.ordem_compra_id === oc.id)
      const contasVinculadas = contasPagar.filter(item => item.ordem_compra_id === oc.id)
      const aprovacoesVinculadas = aprovacoes.filter(item => item.entidade_tipo === 'ORDEM_COMPRA' && item.entidade_id === oc.id)
      await persistDeletedVersions('itens_ordem_compra', itensVinculados as unknown as Record<string, unknown>[])
      await persistDeletedVersions('contas_pagar', contasVinculadas as unknown as Record<string, unknown>[])
      await persistDeletedVersions('aprovacoes', aprovacoesVinculadas as unknown as Record<string, unknown>[])
      await persistDeletedVersion('ordens_compra', oc as unknown as Record<string, unknown>)
    } catch {
      showToast('Erro, tente novamente mais tarde.')
      return
    }
    estornarComprometido(oc)
    setOrdensCompra(prev => prev.filter(item => item.id !== oc.id))
    setItensOrdemCompra(prev => prev.filter(item => item.ordem_compra_id !== oc.id))
    setContasPagar(prev => prev.filter(item => item.ordem_compra_id !== oc.id))
    setAprovacoes(prev => prev.filter(item => !(item.entidade_tipo === 'ORDEM_COMPRA' && item.entidade_id === oc.id)))
    addLog('ORDEM_COMPRA', oc.id, 'CANCELADO', `OC ${oc.numero_oc} excluida`)
    showToast('OC excluida.')
  }

  function gerarConta(oc: OrdemCompra) {
    const jaExiste = contasPagar.some(c => c.ordem_compra_id === oc.id)
    if (jaExiste) {
      showToast('Conta a pagar ja existe para esta OC.')
      return
    }
    const primeiroItem = itensOrdemCompra.find(i => i.ordem_compra_id === oc.id)
    const itemOrcado = itensOrcados.find(i => i.id === primeiroItem?.item_orcado_id)
    const novaConta: ContaPagar = {
      id: 'cp' + Date.now(),
      ordem_compra_id: oc.id,
      obra_id: oc.obra_id,
      centro_custo_id: oc.centro_custo_id,
      categoria_orcamentaria_id: itemOrcado?.categoria_orcamentaria_id || '',
      fornecedor_id: oc.fornecedor_id,
      numero_titulo: gerarCodigo(),
      descricao: `Conta automatica da OC ${oc.numero_oc}`,
      data_emissao: oc.data_emissao,
      data_vencimento: oc.previsao_entrega || oc.data_emissao,
      valor_previsto: oc.valor_total,
      valor_pago: 0,
      status: 'PENDENTE',
      origem: 'ORDEM_COMPRA',
      createdAt: nowISO(),
    }
    setContasPagar(prev => [novaConta, ...prev])
    addLog('CONTA_PAGAR', novaConta.id, 'CRIADO', `Conta gerada automaticamente da OC ${oc.numero_oc}`)
    showToast('Conta a pagar gerada automaticamente.')
  }

  function avancar(oc: OrdemCompra) {
    if (oc.status === 'EM_APROVACAO') {
      const rejeitada = aprovacoes.some(a => a.entidade_tipo === 'ORDEM_COMPRA' && a.entidade_id === oc.id && a.status === 'REJEITADO')
      const aprovada = aprovacoes.some(a => a.entidade_tipo === 'ORDEM_COMPRA' && a.entidade_id === oc.id && a.status === 'APROVADO')
      if (rejeitada) {
        setOrdensCompra(prev => prev.map(x => x.id === oc.id ? { ...x, status: 'CANCELADA' } : x))
        showToast('OC rejeitada no workflow.')
        return
      }
      if (!aprovada) {
        showToast('Aguardando aprovacao financeira.')
        return
      }
      setOrdensCompra(prev => prev.map(x => x.id === oc.id ? { ...x, status: 'APROVADA' } : x))
      showToast('OC aprovada. Agora pode ser emitida.')
      return
    }

    if (oc.status === 'APROVADA') {
      setOrdensCompra(prev => prev.map(x => x.id === oc.id ? { ...x, status: 'EMITIDA' } : x))
      const itens = itensOrdemCompra.filter(i => i.ordem_compra_id === oc.id)
      setItensOrcados(prev => prev.map(item => {
        const valorComprometido = itens.filter(i => i.item_orcado_id === item.id).reduce((s, i) => s + i.valor_total, 0)
        if (!valorComprometido) return item
        const novoComp = item.valor_comprometido + valorComprometido
        return { ...item, valor_comprometido: novoComp, saldo_atual: item.valor_total_previsto - novoComp - item.valor_pago }
      }))
      const cot = cotacoes.find(c => c.id === oc.cotacao_id)
      if (cot) setRequisicoes(prev => prev.map(r => r.id === cot.requisicao_id ? { ...r, status: 'CONVERTIDA_OC' } : r))
      gerarConta(oc)
      addLog('ORDEM_COMPRA', oc.id, 'APROVADO', `OC ${oc.numero_oc} emitida e orcamento comprometido por item`)
      showToast('OC emitida, orcamento comprometido e conta gerada.')
      return
    }

    if (oc.status === 'EMITIDA') {
      setOrdensCompra(prev => prev.map(x => x.id === oc.id ? { ...x, status: 'RECEBIDA' } : x))
      showToast('OC marcada como recebida.')
    }
  }

  const cotName = (id: string) => cotacoes.find(c => c.id === id)?.numero_cotacao || '-'
  const fornName = (id: string) => fornecedores.find(f => f.id === id)?.nome_fantasia || '-'
  const obraName = (id: string) => obras.find(o => o.id === id)?.nome_obra || '-'
  const ccName = (id: string) => centrosCusto.find(c => c.id === id)?.nome || '-'

  return (
    <div>
      {toast && <div style={{ position: 'fixed', top: 20, right: 24, background: '#22c55e', color: '#fff', padding: '10px 18px', borderRadius: 8, fontSize: 13, zIndex: 9999, fontWeight: 500 }}>{toast}</div>}

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <div>
          <h1 style={{ color: '#f7f8f8', fontSize: 18, fontWeight: 600, margin: 0 }}>Ordens de Compra</h1>
          <p style={{ color: '#62666d', fontSize: 13, margin: '2px 0 0' }}>{ordensCompra.length} ordens</p>
        </div>
        <button onClick={openNew} style={{ background: '#f0403b', color: '#fff', border: 'none', borderRadius: 7, padding: '8px 16px', fontSize: 13, cursor: 'pointer', fontWeight: 500 }}>+ Nova OC</button>
      </div>

      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead><tr style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
          {['Numero OC', 'Cotacao', 'Fornecedor', 'Obra', 'Centro', 'Itens', 'Valor', 'Status', 'Acoes'].map(h => <th key={h} style={{ textAlign: 'left', padding: '8px 12px', color: '#62666d', fontWeight: 500 }}>{h}</th>)}
        </tr></thead>
        <tbody>
          {ordensCompra.map(oc => {
            const itens = itensOrdemCompra.filter(i => i.ordem_compra_id === oc.id)
            return (
              <tr key={oc.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }} className="table-row">
                <td style={{ padding: '10px 12px', color: '#ffb4b1', fontFamily: 'monospace', fontSize: 11 }}>{oc.numero_oc}</td>
                <td style={{ padding: '10px 12px', color: '#a0a4ab' }}>{cotName(oc.cotacao_id)}</td>
                <td style={{ padding: '10px 12px', color: '#f7f8f8', fontWeight: 500 }}>{fornName(oc.fornecedor_id)}</td>
                <td style={{ padding: '10px 12px', color: '#a0a4ab' }}>{obraName(oc.obra_id)}</td>
                <td style={{ padding: '10px 12px', color: '#a0a4ab' }}>{ccName(oc.centro_custo_id)}</td>
                <td style={{ padding: '10px 12px', color: '#a0a4ab' }}>{itens.length}</td>
                <td style={{ padding: '10px 12px', color: '#4ade80', fontWeight: 600 }}>{formatCurrency(oc.valor_total)}</td>
                <td style={{ padding: '10px 12px' }}>
                  <span style={{ background: (STATUS_COLOR[oc.status] || '#94a3b8') + '22', color: STATUS_COLOR[oc.status] || '#94a3b8', borderRadius: 5, padding: '2px 8px', fontSize: 11, fontWeight: 600 }}>{oc.status.replace(/_/g, ' ')}</span>
                </td>
                <td style={{ padding: '10px 12px' }}>
                  <button onClick={() => openEdit(oc)} style={{ background: '#1c1d24', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 5, padding: '4px 10px', color: '#ffb4b1', fontSize: 12, cursor: 'pointer', marginRight: 6 }}>Editar</button>
                  {['EM_APROVACAO', 'APROVADA', 'EMITIDA'].includes(oc.status) && (
                    <button onClick={() => avancar(oc)} style={{ background: '#1c1d24', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 5, padding: '4px 10px', color: '#ffb4b1', fontSize: 12, cursor: 'pointer' }}>Avancar</button>
                  )}
                  <button onClick={() => deleteOrdem(oc)} style={{ background: '#1c1d24', border: '1px solid rgba(248,113,113,0.25)', borderRadius: 5, padding: '4px 10px', color: '#f87171', fontSize: 12, cursor: 'pointer', marginLeft: 6 }}>Excluir</button>
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>

      {modal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.72)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#16171e', border: '1px solid rgba(240,64,59,0.35)', borderRadius: 10, padding: 24, width: 520 }}>
            <h2 style={{ color: '#f7f8f8', fontSize: 16, fontWeight: 600, margin: '0 0 18px' }}>{editId ? 'Editar Ordem de Compra' : 'Nova Ordem de Compra'}</h2>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <div style={{ gridColumn: 'span 2' }}>
                <label className="label-field">Cotacao concluida *</label>
                <select className="input-field" value={form.cotacao_id} onChange={e => setForm(p => ({ ...p, cotacao_id: e.target.value }))}>
                  <option value="">Selecione...</option>
                  {cotConcluidas.map(c => <option key={c.id} value={c.id}>{c.numero_cotacao} - {fornName(c.fornecedor_vencedor_id || '')} - {formatCurrency(cotacaoTotal(c.id))}</option>)}
                </select>
                {errors.cotacao_id && <p style={{ color: '#f87171', fontSize: 11 }}>{errors.cotacao_id}</p>}
              </div>
              <div>
                <label className="label-field">Emissao</label>
                <input className="input-field" type="date" value={form.data_emissao} onChange={e => setForm(p => ({ ...p, data_emissao: e.target.value }))} />
              </div>
              <div>
                <label className="label-field">Previsao entrega *</label>
                <input className="input-field" type="date" value={form.previsao_entrega} onChange={e => setForm(p => ({ ...p, previsao_entrega: e.target.value }))} />
                {errors.previsao_entrega && <p style={{ color: '#f87171', fontSize: 11 }}>{errors.previsao_entrega}</p>}
              </div>
              <div style={{ gridColumn: 'span 2' }}>
                <label className="label-field">Condicao pagamento</label>
                <input className="input-field" value={form.condicao_pagamento} onChange={e => setForm(p => ({ ...p, condicao_pagamento: e.target.value }))} />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 24 }}>
              <button onClick={() => setModal(false)} className="btn-ghost" disabled={saving}>Cancelar</button>
              <button onClick={() => void save()} className="btn-primary" disabled={saving}>{saving ? 'Salvando...' : editId ? 'Salvar alteracoes' : 'Criar OC'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
